import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FlightProvider, useFlight } from './contexts/FlightContext';
import LoginScreen from './screens/LoginScreen';
import FlightSelectionScreen from './screens/FlightSelectionScreen';
import MainLayout from './components/MainLayout';
import { Tab, PwaIcon, NotificationStation } from './types';
import { CheckIcon, XCircleIcon, BellIcon, PaperAirplaneIcon, WifiSlashIcon, UsersIcon, BriefcaseIcon, IdentificationIcon, ShoppingCartIcon, ArchiveBoxIcon, TicketIcon, LockClosedIcon, TruckIcon, CheckBadgeIcon, ClipboardDocumentCheckIcon, XMarkIcon, ArrowSloganIcon } from './components/Icons';
import { database } from './services/firebase';
import { ref, onValue } from 'firebase/database';
import InstallPwaPrompt from './components/InstallPwaPrompt';

const pwaIconSvg = (withBg: boolean) => {
    const bg = withBg ? "<rect width='100' height='100' fill='%231e293b'/>" : "";
    // A more distinct aircraft icon path
    const path = "M50 10 L60 30 L90 40 L60 50 L50 90 L40 50 L10 40 L40 30 Z";
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><defs><linearGradient id='plane-gradient' x1='0%25' y1='0%25' x2='100%25' y2='100%25'><stop offset='0%25' stop-color='%233b82f6' /><stop offset='100%25' stop-color='%2322d3ee' /></linearGradient></defs>${bg}<path d='${path}' fill='url(%23plane-gradient)' /></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const defaultPwaIcons: PwaIcon[] = [
    {
      "src": pwaIconSvg(true),
      "type": "image/svg+xml",
      "sizes": "192x192",
      "purpose": "any"
    },
    {
      "src": pwaIconSvg(true),
      "type": "image/svg+xml",
      "sizes": "512x512",
      "purpose": "any"
    },
    {
      "src": pwaIconSvg(false),
      "type": "image/svg+xml",
      "sizes": "192x192",
      "purpose": "maskable"
    },
     {
      "src": pwaIconSvg(false),
      "type": "image/svg+xml",
      "sizes": "512x512",
      "purpose": "maskable"
    }
];

const getStationIcon = (station: NotificationStation) => {
    switch (station) {
        case 'Crew Arrived':
        case '1st Pax IN':
        case 'Last Pax':
            return <UsersIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case '1st X-ray':
            return <BriefcaseIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Immigration In':
        case 'Immigration Out':
            return <IdentificationIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Duty Free':
            return <ShoppingCartIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'At Gate':
            return <ArchiveBoxIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'At Aircraft':
            return <PaperAirplaneIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Ready For Boarding':
            return <TicketIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Doors Closed':
            return <LockClosedIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Push Back':
            return <TruckIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Acceptance Finalized':
            return <CheckBadgeIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'System Clear':
        case 'Gate Clear':
            return <ClipboardDocumentCheckIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        default:
            return <BellIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
    }
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <FlightProvider>
        <AppContent />
      </FlightProvider>
    </AuthProvider>
  );
};

const LoadingIndicator: React.FC = () => (
    <div className="min-h-screen flex flex-col items-center justify-center text-center bg-slate-900">
        <ArrowSloganIcon className="h-24 w-auto animate-pulse" style={{ animationDuration: '2s' }} />
        <h2 className="mt-6 text-2xl font-bold text-slate-200 animate-slide-up-fade-in">Preparing Dashboard...</h2>
        <p className="mt-2 text-slate-400 animate-slide-up-fade-in" style={{ animationDelay: '150ms' }}>Syncing real-time flight data.</p>
    </div>
);

const AppContent: React.FC = () => {
  const { user, pwaIcons } = useAuth();
  const { selectedFlightData, notification: localNotification, areNotificationsEnabled, isLoading: isFlightDataLoading } = useFlight();
  const [showTour, setShowTour] = useState(false);
  const [isTourInitialized, setIsTourInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  
  // State for global notifications from other users
  const [globalNotification, setGlobalNotification] = useState<{ station: NotificationStation; triggeredBy: string } | null>(null);
  const globalNotificationTimer = useRef<number | null>(null);
  const lastGlobalNotificationTimestamp = useRef<number | null>(null);

  useEffect(() => {
    const iconsToUse = pwaIcons && pwaIcons.length > 0 ? pwaIcons : defaultPwaIcons;
  
    // --- Store icons in IndexedDB for the Service Worker ---
    // The manifest itself is now served by the service worker.
    const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open('jt-crew-db', 1);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        request.onupgradeneeded = (event) => {
            const db = (event.target as any).result;
            if (!db.objectStoreNames.contains('pwa-config')) {
                db.createObjectStore('pwa-config');
            }
        };
    });
  
    const updatePwaConfig = async (key: string, value: any) => {
        try {
            const db = await dbPromise;
            const transaction = db.transaction('pwa-config', 'readwrite');
            const store = transaction.objectStore('pwa-config');
            store.put(value, key);
        } catch (error) {
            console.error("Failed to update IndexedDB:", error);
        }
    };
    updatePwaConfig('icons', iconsToUse);
      
    // --- Dynamically update favicon ---
    // Clean up previous dynamic favicon to prevent duplicates on hot reload
    document.querySelectorAll('[data-dynamic-pwa-favicon]').forEach(el => el.remove());
  
    // Find a suitable icon for favicon, prioritizing maskable (transparent background) to match login screen icon
    const favicon = iconsToUse.find(icon => icon.sizes.includes('192') && icon.purpose === 'maskable') 
                    || iconsToUse.find(icon => icon.sizes.includes('192'));
    if (favicon) {
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = favicon.src;
      faviconLink.setAttribute('sizes', favicon.sizes);
      faviconLink.type = favicon.type;
      faviconLink.setAttribute('data-dynamic-pwa-favicon', 'true');
      document.head.appendChild(faviconLink);
    }
  }, [pwaIcons]);


  useEffect(() => {
    const connectedRef = ref(database, '.info/connected');
    const unsubscribe = onValue(connectedRef, (snapshot) => {
        setIsOnline(snapshot.val() === true);
    });

    return () => {
        unsubscribe();
        if (globalNotificationTimer.current) {
            clearTimeout(globalNotificationTimer.current);
        }
    };
  }, []);

  // Effect to handle incoming remote notifications for visual toasts
  useEffect(() => {
    if (!selectedFlightData) return;
    const remoteNotification = selectedFlightData.lastNotification;
    if (!remoteNotification) return;

    // --- Visual Toast Logic for other users ---
    // Show a visual toast only if the notification is new and from another user
    if (remoteNotification.timestamp !== lastGlobalNotificationTimestamp.current) {
        lastGlobalNotificationTimestamp.current = remoteNotification.timestamp;
        
        if (remoteNotification.triggeredBy !== user?.username) {
            setGlobalNotification({
                station: remoteNotification.station,
                triggeredBy: remoteNotification.triggeredBy,
            });

            if (globalNotificationTimer.current) {
                clearTimeout(globalNotificationTimer.current);
            }
            globalNotificationTimer.current = window.setTimeout(() => {
                setGlobalNotification(null);
            }, 7000); // Show for 7 seconds
        }
    }
  }, [selectedFlightData, user?.username]);


  useEffect(() => {
    if (user && !isTourInitialized) {
      const hasCompletedTour = localStorage.getItem('hasCompletedTour');
      if (!hasCompletedTour) {
        setShowTour(true);
      }
      setIsTourInitialized(true);
    }
  }, [user, isTourInitialized]);

  const handleTourComplete = () => {
    localStorage.setItem('hasCompletedTour', 'true');
    setShowTour(false);
  };
  
  const handleDismissGlobalNotification = () => {
      if (globalNotificationTimer.current) {
          clearTimeout(globalNotificationTimer.current);
      }
      setGlobalNotification(null);
  };

  if (!user) {
    return <LoginScreen />;
  }

  if (isFlightDataLoading) {
      return <LoadingIndicator />;
  }
  
  const isFlightSessionActive = Object.keys(useFlight().activeFlights).length > 0;

  if (!isFlightSessionActive) {
    return <FlightSelectionScreen />;
  }

  const roleDefaultTabs: { [key: string]: Tab } = {
    ADMIN: Tab.Admin,
    SUPERVISOR: Tab.CounterDistribution,
    AGENT: Tab.CrewMovement,
    VIEWER: Tab.Overview,
  };
  
  const initialTab = roleDefaultTabs[user.role] || Tab.Overview;

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={`fixed top-0 left-0 right-0 bg-amber-600 text-white text-center text-sm py-1.5 z-[60] shadow-lg transition-transform duration-300 ease-in-out ${isOnline ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="container mx-auto flex items-center justify-center gap-2">
          <WifiSlashIcon className="w-5 h-5"/>
          <span>You are offline. Changes will sync when reconnected.</span>
        </div>
      </div>
      <MainLayout initialTab={initialTab} showTour={showTour} onTourComplete={handleTourComplete} />
      {/* Local notifications (for user's own actions) */}
      {localNotification && (
        <div role="alert" className={`toast-notification fixed top-5 right-5 z-50 p-4 rounded-lg shadow-lg text-white ${localNotification.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600 border border-green-400' : 'bg-gradient-to-r from-red-500 to-rose-600 border border-red-400'}`}>
          <div className="flex items-center">
            {localNotification.type === 'success' ? <CheckIcon className="w-6 h-6 mr-2"/> : <XCircleIcon className="w-6 h-6 mr-2"/>}
            {localNotification.message}
          </div>
        </div>
      )}
      {/* Global notifications (from other users) */}
      {globalNotification && areNotificationsEnabled && (
        <div role="alert" className="toast-notification fixed top-5 left-1/2 -translate-x-1/2 z-50 p-4 rounded-lg shadow-2xl text-white bg-gradient-to-r from-sky-600 to-cyan-600 border border-sky-400 max-w-md w-full">
           <div className="flex items-center">
            {getStationIcon(globalNotification.station)}
            <div className="flex-grow">
                <p className="font-bold text-lg">{globalNotification.station} recorded</p>
                <p className="text-sm text-sky-200">by {globalNotification.triggeredBy}</p>
            </div>
            <button onClick={handleDismissGlobalNotification} className="ml-4 p-1 rounded-full text-sky-200 hover:bg-sky-700/50 transition-colors flex-shrink-0" aria-label="Dismiss notification">
                <XMarkIcon className="w-5 h-5"/>
            </button>
           </div>
        </div>
      )}
      <InstallPwaPrompt />
    </>
  );
};

export default App;